'use client'

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdCampaign,
    MdAdd,
    MdEdit,
    MdDelete,
    MdSearch,
    MdClose,
    MdChevronLeft,
    MdChevronRight,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import ConfirmModal from '@/components/confirm-modal'
import {
    getCampaigns,
    deleteCampaign,
    Campaign,
} from '@/lib/api/marketing/campaign-api'

const STATUS_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'DRAFT', label: 'Rascunho' },
    { value: 'ACTIVE', label: 'Ativa' },
    { value: 'PAUSED', label: 'Pausada' },
    { value: 'FINISHED', label: 'Finalizada' },
]

const STATUS_BADGE: Record<string, { label: string; class: string }> = {
    DRAFT: { label: 'Rascunho', class: 'badge-ghost' },
    ACTIVE: { label: 'Ativa', class: 'badge-success' },
    PAUSED: { label: 'Pausada', class: 'badge-warning' },
    FINISHED: { label: 'Finalizada', class: 'badge-info' },
}

function formatDate(dateStr: string | null) {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('pt-BR')
}

function formatCurrency(value: number) {
    return (value / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

export default function CampaignsListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'marketing',
        'campaigns'
    )

    const [campaigns, setCampaigns] = useState<Campaign[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [total, setTotal] = useState(0)

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [campaignToDelete, setCampaignToDelete] = useState<number | null>(
        null
    )
    const [deleting, setDeleting] = useState(false)

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        loadCampaigns()
    }, [page, statusFilter])

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            setPage(1)
            loadCampaigns()
        }, 400)
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [searchTerm])

    const loadCampaigns = async () => {
        setLoading(true)
        try {
            const response = await getCampaigns({
                page,
                limit: 15,
                search: searchTerm.trim() || undefined,
                status: statusFilter || undefined,
            })
            if (response.success) {
                setCampaigns(response.result.data)
                setTotalPages(response.result.totalPages)
                setTotal(response.result.total)
            } else {
                toast.error(response.error || 'Erro ao carregar campanhas')
            }
        } catch {
            toast.error('Erro ao carregar campanhas')
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteClick = (id: number, e: React.MouseEvent) => {
        e.stopPropagation()
        setCampaignToDelete(id)
        setShowDeleteModal(true)
    }

    const handleDelete = async () => {
        if (!campaignToDelete) return

        setDeleting(true)
        try {
            const response = await deleteCampaign(campaignToDelete)
            if (response.success) {
                toast.success('Campanha removida com sucesso')
                setShowDeleteModal(false)
                setCampaignToDelete(null)
                loadCampaigns()
            } else {
                toast.error(response.error || 'Erro ao remover campanha')
            }
        } catch {
            toast.error('Erro ao remover campanha')
        } finally {
            setDeleting(false)
        }
    }

    if (loading && campaigns.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando campanhas...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdCampaign className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Campanhas
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total}{' '}
                            {total === 1
                                ? 'campanha cadastrada'
                                : 'campanhas cadastradas'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/marketing/campaigns/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Nova Campanha</span>
                    </button>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                    <select
                        className="select select-bordered w-full sm:w-48"
                        value={statusFilter}
                        onChange={e => {
                            setStatusFilter(e.target.value)
                            setPage(1)
                        }}
                    >
                        {STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {campaigns.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdCampaign className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm || statusFilter
                                ? 'Nenhuma campanha encontrada'
                                : 'Nenhuma campanha cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm || statusFilter
                                ? 'Tente ajustar os filtros de busca'
                                : 'Comece criando sua primeira campanha de marketing'}
                        </p>
                        {!searchTerm && !statusFilter && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/marketing/campaigns/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Criar primeira campanha
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50 shadow-sm">
                        <table className="table table-zebra w-full">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Status</th>
                                    <th>Período</th>
                                    <th className="text-right">Cliques</th>
                                    <th className="text-right">Conversões</th>
                                    <th className="text-right">Receita</th>
                                    {(canUpdate || canDelete) && (
                                        <th className="text-right">Ações</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.map(campaign => {
                                    const status = STATUS_BADGE[
                                        campaign.status
                                    ] || {
                                        label: campaign.status,
                                        class: 'badge-ghost',
                                    }
                                    return (
                                        <tr
                                            key={campaign.id}
                                            className="hover cursor-pointer"
                                            onClick={() =>
                                                navigate(
                                                    `/marketing/campaigns/${campaign.id}`
                                                )
                                            }
                                        >
                                            <td>
                                                <div>
                                                    <p className="font-semibold text-base-content">
                                                        {campaign.name}
                                                    </p>
                                                    {campaign.description && (
                                                        <p className="text-xs text-base-content/60 truncate max-w-xs">
                                                            {
                                                                campaign.description
                                                            }
                                                        </p>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-sm ${status.class}`}
                                                >
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td className="text-sm text-base-content/70">
                                                {formatDate(campaign.startDate)}{' '}
                                                - {formatDate(campaign.endDate)}
                                            </td>
                                            <td className="text-right tabular-nums">
                                                {campaign.totalClicks ?? 0}
                                            </td>
                                            <td className="text-right tabular-nums">
                                                {campaign.totalConversions ?? 0}
                                            </td>
                                            <td className="text-right tabular-nums">
                                                {formatCurrency(
                                                    campaign.totalRevenue ?? 0
                                                )}
                                            </td>
                                            {(canUpdate || canDelete) && (
                                                <td className="text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        {canUpdate && (
                                                            <button
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    navigate(
                                                                        `/marketing/campaigns/${campaign.id}/edit`
                                                                    )
                                                                }}
                                                                className="btn btn-xs btn-ghost"
                                                                title="Editar"
                                                            >
                                                                <MdEdit className="text-base" />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={e =>
                                                                    handleDeleteClick(
                                                                        campaign.id,
                                                                        e
                                                                    )
                                                                }
                                                                className="btn btn-xs btn-ghost text-error hover:bg-error/10"
                                                                title="Remover"
                                                            >
                                                                <MdDelete className="text-base" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2">
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={page <= 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                <MdChevronLeft className="text-xl" />
                            </button>
                            <span className="text-sm text-base-content/70">
                                Página {page} de {totalPages}
                            </span>
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={page >= totalPages}
                                onClick={() => setPage(p => p + 1)}
                            >
                                <MdChevronRight className="text-xl" />
                            </button>
                        </div>
                    )}
                </>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setCampaignToDelete(null)
                }}
                onConfirm={handleDelete}
                title="Remover Campanha"
                message="Tem certeza que deseja remover esta campanha?"
                loading={deleting}
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
